package br.com.texeratx.boardgametimeapp.ui.screens.myclans

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.ContentCopy
import androidx.compose.material.icons.filled.MoreVert
import androidx.compose.material3.Button
import androidx.compose.material3.Card
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import br.com.texeratx.boardgametimeapp.core.session.SessionManager
import br.com.texeratx.boardgametimeapp.data.model.clan.ClanInviteDTO
import br.com.texeratx.boardgametimeapp.data.model.clan.ClanJoinRequestDTO
import br.com.texeratx.boardgametimeapp.data.model.clan.ClanMemberDTO
import br.com.texeratx.boardgametimeapp.data.repository.ClanRepository
import br.com.texeratx.boardgametimeapp.ui.components.PlayerAvatar
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppScreenScaffold
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppTopBarColors
import cafe.adriel.voyager.core.model.ScreenModel
import cafe.adriel.voyager.core.model.screenModelScope
import cafe.adriel.voyager.core.screen.Screen
import cafe.adriel.voyager.koin.getScreenModel
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.currentOrThrow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import org.koin.core.parameter.parametersOf

data class ClanMembersUiState(
    val isLoading: Boolean = false,
    val members: List<ClanMemberDTO> = emptyList(),
    val invites: List<ClanInviteDTO> = emptyList(),
    val pendingRequests: List<ClanJoinRequestDTO> = emptyList(),
    val error: String? = null,
    val lastInvite: ClanInviteDTO? = null,
    val currentUserRole: String? = null,
)

class ClanMembersViewModel(
    private val repo: ClanRepository,
    private val sessionManager: SessionManager,
    private val clanId: Long
) : ScreenModel {

    private val _state = MutableStateFlow(ClanMembersUiState())
    val state = _state.asStateFlow()

    fun load() {
        screenModelScope.launch {
            _state.value = _state.value.copy(isLoading = true, error = null)

            val session = sessionManager.load()
            val membersResult  = repo.listMembers(clanId)
            val invitesResult  = repo.listInvites(clanId)
            val pendingResult  = repo.listPendingJoinRequests(clanId)

            val errors = listOfNotNull(
                membersResult.exceptionOrNull()?.let { "membros: ${it.message}" },
                invitesResult.exceptionOrNull()?.let { "convites: ${it.message}" },
                pendingResult.exceptionOrNull()?.let { "pedidos: ${it.message}" }
            )

            _state.value = _state.value.copy(
                isLoading = false,
                currentUserRole = session?.activeRole,
                members = membersResult.getOrDefault(emptyList()),
                invites = invitesResult.getOrDefault(emptyList()),
                pendingRequests = pendingResult.getOrDefault(emptyList()),
                error = errors.joinToString("\n").takeIf { it.isNotBlank() }
            )
        }
    }

    fun createInvite() {
        screenModelScope.launch {
            repo.createInvite(clanId, null, null).fold(
                onSuccess = { inv ->
                    _state.value = _state.value.copy(
                        lastInvite = inv,
                        invites = listOf(inv) + _state.value.invites
                    )
                },
                onFailure = { e ->
                    _state.value = _state.value.copy(error = e.message)
                }
            )
        }
    }

    fun revokeInvite(inviteId: Long) {
        screenModelScope.launch {
            repo.revokeInvite(clanId, inviteId)
            load()
        }
    }

    fun approve(requestId: Long) {
        screenModelScope.launch {
            repo.approveJoinRequest(clanId, requestId)
            load()
        }
    }

    fun reject(requestId: Long) {
        screenModelScope.launch {
            repo.rejectJoinRequest(clanId, requestId)
            load()
        }
    }

    fun updateRole(userId: Long, newRole: String) {
        screenModelScope.launch {
            repo.updateMemberRole(clanId, userId, newRole).fold(
                onSuccess = { load() },
                onFailure = { e -> _state.value = _state.value.copy(error = e.message) }
            )
        }
    }

    fun removeMember(userId: Long) {
        screenModelScope.launch {
            repo.removeMember(clanId, userId)
            load()
        }
    }
}

@Composable
private fun MemberCard(
    member: ClanMemberDTO,
    currentUserRole: String?,
    onUpdateRole: (String) -> Unit,
    onRemove: () -> Unit
) {
    val canManage = currentUserRole == "OWNER" || currentUserRole == "ADMIN"
    val canEditThisMember = member.role != "OWNER" &&
        !(currentUserRole == "ADMIN" && member.role == "ADMIN")

    var menuExpanded by remember { mutableStateOf(false) }

    Card(shape = RoundedCornerShape(12.dp), modifier = Modifier.fillMaxWidth()) {
        Row(Modifier.padding(10.dp), verticalAlignment = Alignment.CenterVertically) {
            PlayerAvatar(imageUrl = member.imageUrl, contentDescription = member.displayName, size = 40.dp)
            Column(
                Modifier
                    .weight(1f)
                    .padding(horizontal = 10.dp)
            ) {
                Text(member.displayName, fontWeight = FontWeight.SemiBold)
                Text(
                    "${member.email} • ${member.role}",
                    style = MaterialTheme.typography.bodySmall,
                    color = when (member.role) {
                        "OWNER" -> MaterialTheme.colorScheme.primary
                        "ADMIN" -> MaterialTheme.colorScheme.secondary
                        else    -> MaterialTheme.colorScheme.onSurfaceVariant
                    }
                )
            }

            if (canManage && canEditThisMember) {
                Box {
                    IconButton(onClick = { menuExpanded = true }) {
                        Icon(Icons.Default.MoreVert, contentDescription = "Opções")
                    }
                    DropdownMenu(
                        expanded = menuExpanded,
                        onDismissRequest = { menuExpanded = false }
                    ) {
                        if (member.role != "ADMIN") {
                            DropdownMenuItem(
                                text = { Text("Tornar ADMIN") },
                                onClick = { menuExpanded = false; onUpdateRole("ADMIN") }
                            )
                        }
                        if (member.role != "MEMBER") {
                            DropdownMenuItem(
                                text = { Text("Tornar MEMBER") },
                                onClick = { menuExpanded = false; onUpdateRole("MEMBER") }
                            )
                        }
                        DropdownMenuItem(
                            text = { Text("Remover do clã", color = MaterialTheme.colorScheme.error) },
                            onClick = { menuExpanded = false; onRemove() }
                        )
                    }
                }
            }
        }
    }
}

class ClanMembersScreen(val clanId: Long) : Screen {

    override val key: String = "ClanMembers-$clanId"

    @OptIn(ExperimentalMaterial3Api::class)
    @Composable
    override fun Content() {
        val navigator = LocalNavigator.currentOrThrow
        val vm = getScreenModel<ClanMembersViewModel> { parametersOf(clanId) }
        val state by vm.state.collectAsState()
        val clipboard = LocalClipboardManager.current

        LaunchedEffect(Unit) { vm.load() }

        AppScreenScaffold(
            topBar = {
                TopAppBar(
                    title = { Text("Membros do clã", fontWeight = FontWeight.Bold) },
                    navigationIcon = {
                        IconButton(onClick = { navigator.pop() }) {
                            Icon(Icons.Default.ArrowBack, contentDescription = "Voltar")
                        }
                    },
                    colors = AppTopBarColors()
                )
            }
        ) { padding ->
            Column(
                Modifier
                    .padding(padding)
                    .fillMaxSize()
                    .padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                state.error?.let { Text(it, color = MaterialTheme.colorScheme.error) }

                // ── Convites ──────────────────────────────────────────────
                Card(shape = RoundedCornerShape(16.dp), modifier = Modifier.fillMaxWidth()) {
                    Column(Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        Text("Convites", fontWeight = FontWeight.Bold)
                        Button(onClick = vm::createInvite) { Text("Gerar novo código") }

                        state.invites.forEach { inv ->
                            Row(
                                Modifier.fillMaxWidth(),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Column(Modifier.weight(1f)) {
                                    Text(
                                        inv.code,
                                        fontWeight = FontWeight.SemiBold,
                                        style = MaterialTheme.typography.bodyMedium
                                    )
                                    Text(
                                        "usos: ${inv.uses}" + (if (inv.revoked) " • revogado" else ""),
                                        style = MaterialTheme.typography.bodySmall,
                                        color = if (inv.revoked)
                                            MaterialTheme.colorScheme.error
                                        else
                                            MaterialTheme.colorScheme.onSurfaceVariant
                                    )
                                }

                                // botão copiar
                                IconButton(onClick = {
                                    clipboard.setText(AnnotatedString(inv.code))
                                }) {
                                    Icon(
                                        Icons.Default.ContentCopy,
                                        contentDescription = "Copiar código",
                                        tint = MaterialTheme.colorScheme.primary
                                    )
                                }

                                if (!inv.revoked) {
                                    OutlinedButton(onClick = { vm.revokeInvite(inv.id) }) {
                                        Text("Revogar")
                                    }
                                }
                            }
                        }
                    }
                }

                // ── Solicitações pendentes ────────────────────────────────
                if (state.pendingRequests.isNotEmpty()) {
                    Card(shape = RoundedCornerShape(16.dp), modifier = Modifier.fillMaxWidth()) {
                        Column(Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                            Text("Solicitações pendentes", fontWeight = FontWeight.Bold)
                            state.pendingRequests.forEach { req ->
                                Row(
                                    Modifier.fillMaxWidth(),
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    PlayerAvatar(
                                        imageUrl = req.userImageUrl,
                                        contentDescription = req.userDisplayName,
                                        size = 36.dp
                                    )
                                    Spacer(Modifier.height(8.dp))
                                    Column(
                                        Modifier
                                            .weight(1f)
                                            .padding(horizontal = 8.dp)
                                    ) {
                                        Text(req.userDisplayName, fontWeight = FontWeight.SemiBold)
                                        Text(req.userEmail, style = MaterialTheme.typography.bodySmall)
                                    }
                                    Button(onClick = { vm.approve(req.id) }) { Text("Aprovar") }
                                    Spacer(Modifier.height(4.dp))
                                    OutlinedButton(onClick = { vm.reject(req.id) }) { Text("Rejeitar") }
                                }
                            }
                        }
                    }
                }

                // ── Lista de membros ──────────────────────────────────────
                Text("Membros (${state.members.size})", fontWeight = FontWeight.Bold)
                LazyColumn(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                    items(state.members) { m ->
                        MemberCard(
                            member = m,
                            currentUserRole = state.currentUserRole,
                            onUpdateRole = { newRole -> vm.updateRole(m.userId, newRole) },
                            onRemove = { vm.removeMember(m.userId) }
                        )
                    }
                }
            }
        }
    }
}
